'use client';

import { c as _c } from "react/compiler-runtime";
import type { CuePhase, FilmFrame } from '@/components/onboarding/onboarding-film';
import type { MotionValue, TargetAndTransition, Transition } from 'framer-motion';
import { motion, useTransform } from 'framer-motion';
import type { ReactNode } from 'react';
import { useLayoutEffect, useRef } from 'react';
import type { Typing } from './pr-v2-onboarding-typing';
import { TYPING } from './pr-v2-onboarding-typing';
import { cueTransition, isCueShown, offsetWithin } from '@/components/onboarding/onboarding-film';
import { cn } from '@/lib/utils';
export type Cue = 'sorted' | 'titleTyped' | 'bodyTyped' | 'meta' | 'mark' | 'link' | 'sent' | 'answer' | 'lit';
export type MiniatureFrame = FilmFrame<Cue>;

// The drawing is laid out for the prototype's stage, a 680 px dialog inside its 1 px border.
const STAGE_WIDTH = 678;
const EASE = [0.25, 0.1, 0.25, 1] as const;
const GLIDE = [0.2, 0.8, 0.2, 1] as const;
const SNAP: Transition = {
  duration: 0
};
// Blend: half the bars stay grey, the rest are a soft purple, blue or pink.
const TOKEN_COLORS = ['rgba(192,132,252,0.6)', '#3f3f46', '#52525b', 'rgba(147,197,253,0.6)', '#3f3f46', 'rgba(244,114,182,0.5)'];
const TREE = [{
  name: 'Coupon lookup',
  files: ['coupons.ts', 'coupons.test.ts']
}, {
  name: 'Pricing math',
  files: ['pricing.ts']
}, {
  name: 'Checkout API',
  files: ['route.ts']
}, {
  name: 'Checkout page',
  files: ['coupon-field.tsx', 'page.tsx']
}];
const TREE_FILES = TREE.flatMap(group => group.files);
const lineRange = (from: number, to: number) => Array.from({
  length: to - from + 1
}, (_, index) => from + index);

// Scatter offsets and token bars derive from an index, so every visit draws the same page.
const scattered = (index: number) => ({
  x: index * 37 % 36 - 20,
  y: index * 53 % 90 - 30 + (index % 2 ? 24 : -12),
  rotate: index * 29 % 14 - 7,
  opacity: 0.55
});
const tokens = (line: number) => Array.from({
  length: 2 + line * 7 % 4
}, (_, index) => ({
  key: `${line}.${index}`,
  width: 10 + (line * 13 + index * 17) % 46,
  background: TOKEN_COLORS[(line + index) % TOKEN_COLORS.length]
}));
function useTypedText(elapsed, t0) {
  const $ = _c(4);
  const {
    text,
    startMs,
    msPerChar
  } = t0;
  let t1;
  if ($[0] !== msPerChar || $[1] !== startMs || $[2] !== text) {
    t1 = ms => text.slice(0, Math.max(0, Math.floor((ms - startMs) / msPerChar)));
    $[0] = msPerChar;
    $[1] = startMs;
    $[2] = text;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return useTransform(elapsed, t1);
}
function typed(phase: CuePhase, text: MotionValue<string>, finished: ReactNode) {
  if (phase === 'later') {
    return null;
  }
  return phase === 'pending' ? <motion.span>{text}</motion.span> : finished;
}
function Code(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <code className="rounded-[2px] bg-[#1f1f24] px-0.5 font-mono text-[7.4px] text-zinc-200">{children}</code>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function TopBar() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex h-[22px] items-center gap-1.5 border-b border-[#18181b] px-2.5 text-[8.5px] text-zinc-500"><span>Pull requests</span><span>›</span><span className="font-medium text-zinc-300">shop #240</span><span>Add coupon codes to checkout</span></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function GroupCard(t0) {
  const $ = _c(41);
  const {
    frame
  } = t0;
  const title = useTypedText(frame.elapsed, TYPING.title);
  const body = useTypedText(frame.elapsed, TYPING.body);
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== body || $[1] !== frame || $[2] !== title) {
    const meta = frame.phase("meta");
    t4 = "rounded-[7px] border border-[#25252b] bg-[#0f0f12] px-2 pb-1.5 pt-[7px]";
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="flex justify-between text-[7.5px] text-[#8b8b93]"><span>Group 1 of 4</span><span>‹ ›</span></div>;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    let t8;
    if ($[12] !== frame || $[13] !== title) {
      t8 = typed(frame.phase("titleTyped"), title, TYPING.title.text);
      $[12] = frame;
      $[13] = title;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    if ($[15] !== t8) {
      t6 = <div className="mt-1 min-h-[13px] text-[10px] font-semibold text-zinc-50">{t8}</div>;
      $[15] = t8;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    let t9;
    if ($[17] !== body || $[18] !== frame) {
      let t10;
      if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = <><Code>findCoupon</Code>{TYPING.body.text.replace("findCoupon", "")}</>;
        $[20] = t10;
      } else {
        t10 = $[20];
      }
      t9 = typed(frame.phase("bodyTyped"), body, t10);
      $[17] = body;
      $[18] = frame;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    if ($[21] !== t9) {
      t7 = <div className="mt-[3px] min-h-[35px] text-[8px] leading-[1.45] text-[#c4c4cc]">{t9}</div>;
      $[21] = t9;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    T0 = motion.div;
    t1 = false;
    const t10 = isCueShown(meta) ? 1 : 0;
    if ($[23] !== t10) {
      t2 = {
        opacity: t10
      };
      $[23] = t10;
      $[24] = t2;
    } else {
      t2 = $[24];
    }
    t3 = cueTransition(meta, {
      duration: 0.4,
      ease: EASE
    });
    $[0] = body;
    $[1] = frame;
    $[2] = title;
    $[3] = T0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    T0 = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
    t6 = $[9];
    t7 = $[10];
  }
  let t10;
  let t11;
  let t12;
  let t8;
  let t9;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span>2 files</span>;
    t9 = <span>·</span>;
    t10 = <span className="text-emerald-400">+69</span>;
    t11 = <span>·</span>;
    t12 = <span data-link-start={true} className="font-semibold text-amber-500">?1</span>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
    $[28] = t8;
    $[29] = t9;
  } else {
    t10 = $[25];
    t11 = $[26];
    t12 = $[27];
    t8 = $[28];
    t9 = $[29];
  }
  let t13;
  if ($[30] !== T0 || $[31] !== t1 || $[32] !== t2 || $[33] !== t3) {
    t13 = <T0 initial={t1} animate={t2} transition={t3} className="mt-[5px] flex items-center gap-1 text-[7.5px] text-zinc-500">{t8}{t9}{t10}{t11}{t12}</T0>;
    $[30] = T0;
    $[31] = t1;
    $[32] = t2;
    $[33] = t3;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t13 || $[36] !== t4 || $[37] !== t5 || $[38] !== t6 || $[39] !== t7) {
    t14 = <div className={t4}>{t5}{t6}{t7}{t13}</div>;
    $[35] = t13;
    $[36] = t4;
    $[37] = t5;
    $[38] = t6;
    $[39] = t7;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  return t14;
}
function Bubble(t0) {
  const $ = _c(10);
  const {
    phase,
    className,
    children
  } = t0;
  let t1;
  let t2;
  if ($[0] !== phase) {
    t1 = isCueShown(phase) ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 6
    };
    t2 = cueTransition(phase, {
      duration: 0.35,
      ease: EASE
    });
    $[0] = phase;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] !== className) {
    t3 = cn("rounded-[7px] py-[5px]", className);
    $[3] = className;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== children || $[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <motion.div initial={false} animate={t1} transition={t2} className={t3}>{children}</motion.div>;
    $[5] = children;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function ChatColumn(t0) {
  const $ = _c(25);
  const {
    frame
  } = t0;
  const question = useTypedText(frame.elapsed, TYPING.question);
  let t1;
  if ($[0] !== frame) {
    t1 = <GroupCard frame={frame} />;
    $[0] = frame;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== frame) {
    t2 = frame.phase("sent");
    $[2] = frame;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <Bubble phase={t2} className="max-w-[120px] self-end bg-[#1f1f24] px-[7px] text-zinc-200">{TYPING.question.text}</Bubble>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== frame) {
    t4 = frame.phase("answer");
    $[6] = frame;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Code>expiresAt</Code>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <br />;
    t7 = <span className="mt-1 inline-block rounded-[4px] border border-[#1e3a5f] bg-[rgba(59,130,246,0.08)] px-1 py-px text-[7px] text-blue-300">coupons.ts · lines 12–14</span>;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t4) {
    t8 = <Bubble phase={t4} className="pl-0.5 pr-[7px] text-zinc-300">It checks {t5} first, so an old code never gets a price.{t6}{t7}</Bubble>;
    $[11] = t4;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t3 || $[14] !== t8) {
    t9 = <div className="absolute inset-x-[7px] top-[116px] flex flex-col gap-1.5 text-[8px] leading-[1.45]">{t3}{t8}</div>;
    $[13] = t3;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== frame || $[17] !== question) {
    t10 = frame.phase("sent") === "pending" ? <><motion.span className="text-zinc-200">{question}</motion.span><motion.span animate={{
        opacity: [1, 1, 0, 0]
      }} transition={{
        duration: 0.9,
        times: [0, 0.5, 0.5, 1],
        repeat: Infinity
      }} className="ml-px inline-block h-[9px] w-px bg-zinc-200 align-[-1px]" /></> : "Ask about this PR\u2026";
    $[16] = frame;
    $[17] = question;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10) {
    t11 = <div className="absolute inset-x-[7px] bottom-2 h-[30px] rounded-[7px] border border-[#26262b] bg-[#141417] px-[7px] py-1.5 text-[8px] text-zinc-500">{t10}</div>;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t1 || $[22] !== t11 || $[23] !== t9) {
    t12 = <div className="relative border-r border-[#18181b] p-[7px]">{t1}{t9}{t11}</div>;
    $[21] = t1;
    $[22] = t11;
    $[23] = t9;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  return t12;
}
function DiffFile(t0) {
  const $ = _c(13);
  const {
    name,
    added,
    animate,
    transition,
    children
  } = t0;
  let t1;
  if ($[0] !== name) {
    t1 = <span className="font-medium text-zinc-200">{name}</span>;
    $[0] = name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span>src/lib/</span>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== added) {
    t3 = <span className="ml-auto text-emerald-400">+{added}</span>;
    $[3] = added;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t1 || $[6] !== t3) {
    t4 = <div className="flex h-[18px] items-center gap-[5px] border-b border-[#1a1a1e] px-[7px] text-[8px] text-zinc-500">{t1}{t2}{t3}</div>;
    $[5] = t1;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== animate || $[9] !== children || $[10] !== t4 || $[11] !== transition) {
    t5 = <motion.div initial={false} animate={animate} transition={transition} className="absolute inset-x-2 top-2 overflow-hidden rounded-[6px] border border-[#1f1f24] bg-[#0c0c0e]">{t4}{children}</motion.div>;
    $[8] = animate;
    $[9] = children;
    $[10] = t4;
    $[11] = transition;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
function DiffLine(t0) {
  const $ = _c(11);
  const {
    line,
    children
  } = t0;
  let t1;
  if ($[0] !== line) {
    t1 = <span className="w-[30px] flex-none pr-1.5 text-right font-mono text-[7px] text-[rgba(74,222,128,0.7)]">{line}</span>;
    $[0] = line;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = 8 + line * 5 % 4 * 8;
  let t3;
  if ($[2] !== t2) {
    t3 = <span style={{
      width: t2
    }} />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== line) {
    t4 = tokens(line).map(_temp);
    $[4] = line;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== children || $[7] !== t1 || $[8] !== t3 || $[9] !== t4) {
    t5 = <div className="relative flex h-3 items-center bg-[rgba(34,197,94,0.06)]">{t1}{t3}{t4}{children}</div>;
    $[6] = children;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
function _temp(t0) {
  const {
    key,
    width,
    background
  } = t0;
  return <span key={key} className="mr-1 h-1 rounded-[2px]" style={{
    width,
    background
  }} />;
}
function Mark(t0) {
  const $ = _c(15);
  const {
    phase
  } = t0;
  let t1;
  let t2;
  if ($[0] !== phase) {
    t1 = isCueShown(phase) ? {
      opacity: 1,
      scale: 1
    } : {
      opacity: 0,
      scale: 0.2
    };
    t2 = cueTransition(phase, {
      scale: {
        duration: 0.45,
        ease: [0.3, 1.6, 0.4, 1]
      },
      opacity: {
        duration: 0.25,
        ease: EASE
      }
    });
    $[0] = phase;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = <motion.span data-link-end={true} initial={false} animate={t1} transition={t2} className="absolute left-0.5 top-px size-2.5 rounded-[3px] border border-[#b45309] bg-[rgba(245,158,11,0.12)] text-center text-[7px] font-bold leading-2 text-amber-500">?</motion.span>;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  let t5;
  if ($[6] !== phase) {
    t4 = phase === "live" ? {
      opacity: [0.9, 0],
      scale: [0.8, 2.2]
    } : {
      opacity: 0
    };
    t5 = phase === "live" ? {
      duration: 1.4,
      ease: "easeOut",
      delay: 0.15,
      repeat: 1
    } : SNAP;
    $[6] = phase;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = <motion.span initial={false} animate={t4} transition={t5} className="absolute -left-px -top-0.5 size-4 rounded-[5px] border border-amber-500" />;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t3 || $[13] !== t6) {
    t7 = <>{t3}{t6}</>;
    $[12] = t3;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}
function DiffColumn(t0) {
  const $ = _c(21);
  const {
    frame
  } = t0;
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== frame) {
    const sorted = frame.phase("sorted");
    const lit = frame.phase("lit");
    const glide = cueTransition(sorted, {
      duration: 0.9,
      ease: GLIDE,
      borderColor: {
        duration: 0.6,
        ease: EASE
      }
    });
    t5 = "relative overflow-hidden";
    t6 = <DiffFile name="coupons.ts" added={40} transition={glide} animate={isCueShown(sorted) ? {
      y: 0,
      borderColor: "#3f3f46"
    } : {
      y: 128,
      borderColor: "#1f1f24"
    }}>{lineRange(8, 19).map(line => <DiffLine key={line} line={line}>{line >= 12 && line <= 14 ? <motion.span initial={false} animate={{
          clipPath: isCueShown(lit) ? "inset(0% 0% 0% 0%)" : "inset(0% 100% 0% 0%)"
        }} transition={cueTransition(lit, {
          duration: 0.7,
          ease: [0.3, 0.7, 0.2, 1]
        })} className="absolute inset-0 bg-[linear-gradient(90deg,rgba(96,165,250,0.28),rgba(96,165,250,0.12))] shadow-[inset_2px_0_0_#60a5fa]" /> : null}{line === 13 ? <Mark phase={frame.phase("mark")} /> : null}</DiffLine>)}</DiffFile>;
    T0 = DiffFile;
    t2 = "pricing.ts";
    t3 = 29;
    t4 = glide;
    t1 = isCueShown(sorted) ? 170 : 0;
    $[0] = frame;
    $[1] = T0;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    T0 = $[1];
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t1) {
    t7 = {
      y: t1
    };
    $[8] = t1;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = lineRange(21, 29).map(_temp2);
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== T0 || $[12] !== t2 || $[13] !== t3 || $[14] !== t4 || $[15] !== t7) {
    t9 = <T0 name={t2} added={t3} transition={t4} animate={t7}>{t8}</T0>;
    $[11] = T0;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t7;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t5 || $[18] !== t6 || $[19] !== t9) {
    t10 = <div className={t5}>{t6}{t9}</div>;
    $[17] = t5;
    $[18] = t6;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
}
function _temp2(line_0) {
  return <DiffLine key={line_0} line={line_0} />;
}
function TreeColumn(t0) {
  const $ = _c(8);
  const {
    phase
  } = t0;
  let t1;
  if ($[0] !== phase) {
    t1 = isCueShown(phase);
    $[0] = phase;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const sorted = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="mb-2 text-[7px] font-semibold leading-none tracking-[0.06em] text-zinc-500">GROUPED (6)</div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== phase || $[4] !== sorted) {
    t3 = TREE.map((group, index) => <div key={group.name} className="mb-[7px]"><motion.div initial={false} animate={sorted ? {
        opacity: 1,
        x: 0
      } : {
        opacity: 0,
        x: -6
      }} transition={cueTransition(phase, {
        duration: 0.4,
        ease: EASE,
        delay: 0.37 + index * 0.12
      })} className="h-[15px] whitespace-nowrap text-[8.5px] font-medium text-zinc-200"><span className="inline-block w-[11px] text-zinc-500">{index + 1}.</span>{group.name}</motion.div>{group.files.map(file => {
        const order = TREE_FILES.indexOf(file);
        const delay = 0.05 + order * 0.07;
        return <motion.div key={file} initial={false} animate={sorted ? {
          x: 0,
          y: 0,
          rotate: 0,
          opacity: 1
        } : scattered(order)} transition={cueTransition(phase, {
          duration: 0.85,
          ease: GLIDE,
          delay,
          opacity: {
            duration: 0.5,
            ease: EASE,
            delay
          }
        })} className="-mr-0.5 mb-px ml-2.5 flex h-[13px] items-center gap-1 whitespace-nowrap text-[8px] text-zinc-400"><span className="h-[7px] w-1.5 flex-none rounded-[1px] border border-zinc-600" />{file}{file === "coupons.ts" ? <span className="ml-auto text-[7.5px] font-bold text-amber-500">?</span> : null}</motion.div>;
      })}</div>);
    $[3] = phase;
    $[4] = sorted;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <div className="relative overflow-hidden border-l border-[#18181b] px-2 py-[7px]">{t2}{t3}</div>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
export function PrV2OnboardingMiniature(t0) {
  const $ = _c(32);
  const {
    frame
  } = t0;
  const stage = useRef(null);
  const drawing = useRef(null);
  const page = useRef(null);
  const link = useRef(null);
  const linkPhase = frame.phase("link");
  const linked = isCueShown(linkPhase);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const outer = stage.current;
      const inner = drawing.current;
      if (!outer || !inner) {
        return;
      }
      const fit = () => {
        inner.style.transform = `scale(${Math.min(1, outer.clientWidth / STAGE_WIDTH)})`;
      };
      fit();
      const observer = new ResizeObserver(fit);
      observer.observe(outer);
      return () => observer.disconnect();
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useLayoutEffect(t1, t2);
  useLayoutEffect(() => {
    const root = page.current;
    const from = root?.querySelector("[data-link-start]");
    const to = root?.querySelector("[data-link-end]");
    if (!linked || !root || !from || !to) {
      return;
    }
    const start = offsetWithin(root, from);
    const end = offsetWithin(root, to);
    const x1 = start.x + from.offsetWidth + 2;
    const y1 = start.y + from.offsetHeight / 2;
    const x2 = end.x - 2;
    const y2 = end.y + to.offsetHeight / 2;
    link.current?.setAttribute("d", `M ${x1} ${y1} C ${x1 + 60} ${y1}, ${x2 - 70} ${y2}, ${x2} ${y2}`);
  }, [linked]);
  const marching = linked && !frame.still;
  const t3 = "true";
  const t4 = "relative aspect-[678/318] overflow-hidden border-b border-[#1d1d22] bg-[radial-gradient(120%_100%_at_50%_0%,#16161b_0%,#0b0b0d_62%)]";
  const t5 = "absolute left-0 top-0 h-[318px] w-[678px] origin-top-left";
  const t6 = "absolute left-6 top-[26px] h-[274px] w-[632px] overflow-hidden rounded-[10px] border border-[#222228] bg-[#09090b]";
  let t7;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <TopBar />;
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  let t8;
  let t9;
  if ($[3] !== frame) {
    t8 = <ChatColumn frame={frame} />;
    t9 = <DiffColumn frame={frame} />;
    $[3] = frame;
    $[4] = t8;
    $[5] = t9;
  } else {
    t8 = $[4];
    t9 = $[5];
  }
  let t10;
  if ($[6] !== frame) {
    t10 = frame.phase("sorted");
    $[6] = frame;
    $[7] = t10;
  } else {
    t10 = $[7];
  }
  let t11;
  if ($[8] !== t10) {
    t11 = <TreeColumn phase={t10} />;
    $[8] = t10;
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  let t12;
  if ($[10] !== t11 || $[11] !== t8 || $[12] !== t9) {
    t12 = <div className="absolute inset-x-0 bottom-0 top-[22px] grid grid-cols-[168px_1fr_150px]">{t8}{t9}{t11}</div>;
    $[10] = t11;
    $[11] = t8;
    $[12] = t9;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  const t13 = "pointer-events-none absolute left-0 top-0 h-[274px] w-[632px] overflow-visible";
  const t14 = motion;
  const t15 = "none";
  const t16 = "#f59e0b";
  const t17 = 1;
  const t18 = "3 3";
  const t19 = false;
  const t20 = linked ? 0.75 : 0;
  let t21;
  if ($[14] !== marching) {
    t21 = marching ? [0, -12] : 0;
    $[14] = marching;
    $[15] = t21;
  } else {
    t21 = $[15];
  }
  let t22;
  if ($[16] !== t20 || $[17] !== t21) {
    t22 = {
      opacity: t20,
      strokeDashoffset: t21
    };
    $[16] = t20;
    $[17] = t21;
    $[18] = t22;
  } else {
    t22 = $[18];
  }
  const t23 = cueTransition(linkPhase, {
    duration: 0.4,
    ease: EASE
  });
  let t24;
  if ($[19] !== marching) {
    t24 = marching ? {
      duration: 1.2,
      ease: "linear",
      repeat: Infinity
    } : SNAP;
    $[19] = marching;
    $[20] = t24;
  } else {
    t24 = $[20];
  }
  let t25;
  if ($[21] !== t23 || $[22] !== t24) {
    t25 = {
      opacity: t23,
      strokeDashoffset: t24
    };
    $[21] = t23;
    $[22] = t24;
    $[23] = t25;
  } else {
    t25 = $[23];
  }
  let t26;
  if ($[24] !== t14.path || $[25] !== t22 || $[26] !== t25) {
    t26 = <svg className={t13}><t14.path ref={link} fill={t15} stroke={t16} strokeWidth={t17} strokeDasharray={t18} initial={t19} animate={t22} transition={t25} /></svg>;
    $[24] = t14.path;
    $[25] = t22;
    $[26] = t25;
    $[27] = t26;
  } else {
    t26 = $[27];
  }
  let t27;
  if ($[28] !== t12 || $[29] !== t26 || $[30] !== t7) {
    t27 = <div ref={stage} aria-hidden={t3} className={t4}><div ref={drawing} className={t5}><div ref={page} className={t6}>{t7}{t12}{t26}</div></div></div>;
    $[28] = t12;
    $[29] = t26;
    $[30] = t7;
    $[31] = t27;
  } else {
    t27 = $[31];
  }
  return t27;
}